"use client";

import * as React from "react";
import { Badge } from "../../badge.js";
import { X, User } from "lucide-react";

export interface AttendeeChipProps {
  email: string;
  name?: string;
  onRemove?: (email: string) => void;
  className?: string;
  variant?: "default" | "secondary" | "outline" | "destructive";
  size?: "sm" | "md" | "lg";
  showRemoveButton?: boolean;
  clickable?: boolean;
  disabled?: boolean;
  ContactPopoverComponent?: React.ComponentType<{ name: string; email: string; children: React.ReactNode }>;
}

export function AttendeeChip({ 
  email, 
  name, 
  onRemove, 
  className = "",
  variant = "secondary",
  size = "md",
  showRemoveButton = false,
  clickable = true,
  disabled = false,
  ContactPopoverComponent
}: AttendeeChipProps) {
  const displayName = name || email;
  
  const chipContent = (
    <Badge 
      variant={variant} 
      className={`
        flex items-center gap-1 transition-colors
        ${clickable && !disabled ? 'cursor-pointer hover:bg-opacity-80' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${size === 'sm' ? 'text-xs px-2 py-1' : size === 'lg' ? 'text-sm px-3 py-2' : 'text-sm px-2 py-1'}
        ${className}
      `}
    >
      <User className={`${size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'}`} />
      <span className="truncate max-w-32" title={displayName}>
        {displayName}
      </span>
      {showRemoveButton && onRemove && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(email);
          }}
          className="ml-1 hover:text-red-500 transition-colors"
          aria-label={`Remove ${email}`}
        >
          <X className={`${size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'}`} />
        </button>
      )}
    </Badge>
  );

  // If clickable and not disabled, wrap with ContactPopoverComponent if provided
  if (clickable && !disabled && ContactPopoverComponent) {
    return (
      <ContactPopoverComponent
        name={displayName}
        email={email}
      >
        {chipContent}
      </ContactPopoverComponent>
    );
  }

  // Otherwise return the chip directly
  return chipContent;
}
